import {
  CUSTOM_PRESET_ID,
  ROLLING_PRESETS,
  SEASON_PRESETS,
  matchPresetIdForWindow,
  type ExtraRollingPresetMatch,
} from "@/features/dashboard/lib/explore-date-presets";
import { formatDashboardWindow } from "@/features/dashboard/lib/format-dashboard-window";

export interface SetDateWindowDisplay {
  presetId: string;
  presetLabel: string;
  rangeLabel: string;
}

type Translate = (key: string, defaultValue: string) => string;

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** Calendar `YYYY-MM-DD` from a stored pair, ISO timestamp, or Prisma Date. */
export function toCalendarYmd(value: string | Date | null | undefined): string | null {
  if (value == null) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const year = value.getUTCFullYear();
    const month = String(value.getUTCMonth() + 1).padStart(2, "0");
    const day = String(value.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  const trimmed = value.trim();
  if (YMD.test(trimmed)) return trimmed;
  if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) return trimmed.slice(0, 10);
  return null;
}

function presetLabelForId(
  presetId: string,
  extraRollingPresets: readonly ExtraRollingPresetMatch[],
  t: Translate,
): string {
  if (presetId === CUSTOM_PRESET_ID) {
    return t("dashboard.dateRangePicker.custom", "Custom");
  }
  const extra = extraRollingPresets.find((preset) => preset.id === presetId);
  if (extra) {
    return t(`dashboard.dateRangePicker.presets.${extra.id}`, extra.defaultLabel ?? extra.id);
  }
  const rolling = ROLLING_PRESETS.find((preset) => preset.id === presetId);
  if (rolling) {
    return t(`dashboard.dateRangePicker.presets.${rolling.id}`, rolling.defaultLabel);
  }
  const season = SEASON_PRESETS.find((preset) => preset.id === presetId);
  if (season) {
    return t(`dashboard.dateRangePicker.presets.${season.id}`, season.defaultLabel);
  }
  return t("dashboard.dateRangePicker.custom", "Custom");
}

export function describeSetDateWindow(
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined,
  t: Translate,
  extraRollingPresets: readonly ExtraRollingPresetMatch[] = [],
  today: Date = new Date(),
): SetDateWindowDisplay | null {
  const start = toCalendarYmd(startDate);
  const end = toCalendarYmd(endDate);
  if (!start || !end) return null;

  const presetId = matchPresetIdForWindow(start, end, today, extraRollingPresets);
  return {
    presetId,
    presetLabel: presetLabelForId(presetId, extraRollingPresets, t),
    rangeLabel: formatDashboardWindow({ startDate: start, endDate: end }),
  };
}
